import type {
  AuditEntry,
  Order,
  OrderStatus,
  Product,
  Reseller,
  SizeStock,
  Wholesaler,
} from "@/types/domain";
import { seededRandom } from "./utils";

// Fixed "now" so demo data is stable and deterministic (no Date.now() drift).
export const DEMO_NOW = new Date("2026-07-05T09:30:00+03:00");

function daysAgo(n: number): string {
  return new Date(DEMO_NOW.getTime() - n * 86_400_000).toISOString();
}
function hoursAgo(n: number): string {
  return new Date(DEMO_NOW.getTime() - n * 3_600_000).toISOString();
}
function minsAgo(n: number): string {
  return new Date(DEMO_NOW.getTime() - n * 60_000).toISOString();
}

const SNEAKER_SIZES = ["38", "39", "40", "41", "42", "43", "44", "45"];

const CHART_ACCENTS = [
  "oklch(0.58 0.11 185)",
  "oklch(0.62 0.14 255)",
  "oklch(0.70 0.15 75)",
  "oklch(0.62 0.16 330)",
  "oklch(0.64 0.14 145)",
];

function sizesFor(seed: string, _kind: string): SizeStock[] {
  return SNEAKER_SIZES.map((size, i) => {
    const r = seededRandom(`${seed}-${size}-${i}`);
    // ~15% of sizes out of stock, rest 0–24
    const qty = r < 0.15 ? 0 : Math.floor(r * 24);
    return { size, qty };
  });
}

// ── Wholesalers ──
export const WHOLESALERS: Wholesaler[] = [
  { id: "wh-hassan", name: "Kamukunji Kicks", market: "Kamukunji", plan: "Pro", productCount: 0, connected: true, lastActiveAt: minsAgo(8), rating: 4.8 },
  { id: "wh-2", name: "Eastleigh Sole Co.", market: "Eastleigh", plan: "Business", productCount: 0, connected: true, lastActiveAt: hoursAgo(2), rating: 4.6 },
  { id: "wh-3", name: "River Road Trainers", market: "River Road", plan: "Pro", productCount: 0, connected: true, lastActiveAt: hoursAgo(5), rating: 4.4 },
  { id: "wh-4", name: "Nairobi Threads", market: "CBD", plan: "Free", productCount: 0, connected: true, lastActiveAt: daysAgo(1), rating: 4.2 },
  { id: "wh-5", name: "Gikomba Sneaker Hub", market: "Gikomba", plan: "Pro", productCount: 0, connected: false, lastActiveAt: daysAgo(3), rating: 4.1 },
  { id: "wh-6", name: "Luthuli Kicks", market: "Luthuli Ave", plan: "Business", productCount: 0, connected: false, lastActiveAt: daysAgo(9), rating: 3.9 },
];

// ── Product catalogue ──
interface Seed {
  name: string;
  brand: string;
  category: "sneakers" | "official" | "casual" | "lifestyle";
  condition: "new" | "like_new" | "good" | "fair";
  description: string;
  price: number;
}
const CATALOGUE: Seed[] = [
  { name: "Air Max 270", brand: "Nike", category: "sneakers", condition: "new", description: "OG colourway, box included. Size runs true.", price: 8500 },
  { name: "Air Force 1 '07", brand: "Nike", category: "sneakers", condition: "like_new", description: "Worn twice, no creases. Box available.", price: 9200 },
  { name: "Ultraboost 22", brand: "Adidas", category: "lifestyle", condition: "new", description: "Brand new with tags. Limited stock.", price: 11000 },
  { name: "Samba OG", brand: "Adidas", category: "casual", condition: "new", description: "Classic colourway. Hot in Nairobi right now.", price: 7800 },
  { name: "Classic Leather", brand: "Reebok", category: "official", condition: "new", description: "Clean white leather. Great for office wear.", price: 6400 },
  { name: "Chuck 70 Hi", brand: "Converse", category: "casual", condition: "like_new", description: "Black hi-top. One owner, very clean.", price: 5600 },
  { name: "Old Skool", brand: "Vans", category: "casual", condition: "good", description: "Side stripe intact. Slight sole wear, priced low.", price: 6900 },
  { name: "574 Core", brand: "New Balance", category: "lifestyle", condition: "new", description: "Grey suede. Popular retail export unit.", price: 8100 },
  { name: "Suede Classic", brand: "Puma", category: "casual", condition: "like_new", description: "Royal blue, super clean. No box.", price: 5900 },
  { name: "Gazelle", brand: "Adidas", category: "lifestyle", condition: "new", description: "Originals pack. Box included, unworn.", price: 7200 },
  { name: "Dunk Low Retro", brand: "Nike", category: "sneakers", condition: "new", description: "Panda colourway. Retail pull, box included.", price: 10500 },
  { name: "Oxford Brogue", brand: "Clarks", category: "official", condition: "new", description: "Genuine leather. Full sole. Formal + smart casual.", price: 7400 },
  { name: "Derby Shoe", brand: "Marks & Spencer", category: "official", condition: "like_new", description: "Black, barely worn. Good for interviews.", price: 4200 },
  { name: "Loafer Slip-on", brand: "Gucci Inspired", category: "official", condition: "good", description: "Brown suede. Signs of wear on heel only.", price: 5600 },
  { name: "Trail Runner XT", brand: "Merrell", category: "casual", condition: "new", description: "Waterproof upper. Great for all-terrain.", price: 9800 },
];

function buildProducts(): Product[] {
  const products: Product[] = [];
  let idx = 0;
  for (const wh of WHOLESALERS) {
    // Each wholesaler stocks a rotating subset of the catalogue.
    const count = wh.plan === "Free" ? 6 : 9 + Math.floor(seededRandom(wh.id) * 4);
    for (let i = 0; i < count; i++) {
      const item = CATALOGUE[(idx + i) % CATALOGUE.length];
      const id = `p-${wh.id}-${i}`;
      const r = seededRandom(id);
      products.push({
        id,
        wholesalerId: wh.id,
        wholesalerName: wh.name,
        wholesalerMarket: wh.market,
        name: item.name,
        brand: item.brand,
        category: item.category,
        description: item.description,
        condition: item.condition,
        imageColor: CHART_ACCENTS[(idx + i) % CHART_ACCENTS.length],
        imageUrls: [],
        sizes: sizesFor(id, item.category),
        price: item.price + Math.round((r - 0.5) * 800),
        visible: r > 0.12, // ~12% kept private
        updatedAt: minsAgo(Math.floor(r * 4000)),
      });
    }
    idx += count;
  }
  return products;
}

export const PRODUCTS: Product[] = buildProducts();

// Backfill product counts on wholesalers.
for (const wh of WHOLESALERS) {
  wh.productCount = PRODUCTS.filter((p) => p.wholesalerId === wh.id).length;
}

// ── Resellers ──
export const RESELLERS: Reseller[] = [
  { id: "rs-amara", name: "Amara Sneaker Studio", plan: "Growth", connections: 5, gmvMonth: 340000, commissionRate: 0.02, joinedAt: daysAgo(210) },
  { id: "rs-2", name: "Kicks by Brian", plan: "Starter", connections: 3, gmvMonth: 128000, commissionRate: 0.03, joinedAt: daysAgo(140) },
  { id: "rs-3", name: "SoleMate KE", plan: "Growth", connections: 6, gmvMonth: 415000, commissionRate: 0.02, joinedAt: daysAgo(320) },
  { id: "rs-4", name: "Wambui Streetwear", plan: "Free", connections: 2, gmvMonth: 46000, commissionRate: 0.04, joinedAt: daysAgo(35) },
  { id: "rs-5", name: "Trap House Nairobi", plan: "Starter", connections: 4, gmvMonth: 152000, commissionRate: 0.03, joinedAt: daysAgo(88) },
];

// ── Orders ──
const ORDER_STATUSES: OrderStatus[] = ["pending", "confirmed", "fulfilled", "fulfilled", "cancelled"];

function buildOrders(): Order[] {
  const orders: Order[] = [];
  const visibleProducts = PRODUCTS.filter((p) => p.visible);
  for (let i = 0; i < 64; i++) {
    const r = seededRandom(`order-${i}`);
    const product = visibleProducts[Math.floor(r * visibleProducts.length)];
    const reseller = RESELLERS[Math.floor(seededRandom(`ro-${i}`) * RESELLERS.length)];
    const inStockSizes = product.sizes.filter((s) => s.qty > 0);
    const size = (inStockSizes[Math.floor(r * inStockSizes.length)] ?? product.sizes[0]).size;
    const qty = 1 + Math.floor(seededRandom(`oq-${i}`) * 3);
    const ageHours = Math.floor(seededRandom(`oa-${i}`) * 24 * 30);
    orders.push({
      id: `ord-${i}`,
      reference: `SS-${1000 + i}`,
      wholesalerId: product.wholesalerId,
      wholesalerName: product.wholesalerName,
      resellerId: reseller.id,
      resellerName: reseller.name,
      productId: product.id,
      productName: product.name,
      brand: product.brand,
      size,
      qty,
      unitPrice: product.price,
      commissionRate: reseller.commissionRate,
      status: ORDER_STATUSES[Math.floor(seededRandom(`os-${i}`) * ORDER_STATUSES.length)],
      createdAt: ageHours < 24 ? hoursAgo(ageHours) : daysAgo(Math.floor(ageHours / 24)),
    });
  }
  return orders.sort((a, b) => +new Date(b.createdAt) - +new Date(a.createdAt));
}

export const ORDERS: Order[] = buildOrders();

// ── Audit log ──
export const AUDIT: AuditEntry[] = [
  { id: "a1", actor: "Kamukunji Kicks", actorRole: "wholesaler", action: "UPDATE", target: "stock", recordId: null, oldData: { quantity: 8 }, newData: { quantity: 14 }, at: minsAgo(8) },
  { id: "a2", actor: "Amara Sneaker Studio", actorRole: "reseller", action: "INSERT", target: "orders", recordId: null, oldData: null, newData: { status: "confirmed" }, at: minsAgo(22) },
  { id: "a3", actor: "Eastleigh Sole Co.", actorRole: "wholesaler", action: "UPDATE", target: "products", recordId: null, oldData: { is_visible: false }, newData: { is_visible: true }, at: hoursAgo(1) },
  { id: "a4", actor: "system", actorRole: "system", action: "INSERT", target: "orders", recordId: null, oldData: null, newData: { unit_price: 2500 }, at: hoursAgo(3) },
  { id: "a5", actor: "SoleMate KE", actorRole: "reseller", action: "INSERT", target: "connections", recordId: null, oldData: null, newData: { status: "active" }, at: hoursAgo(6) },
  { id: "a6", actor: "Njeri", actorRole: "admin", action: "UPDATE", target: "profiles", recordId: null, oldData: { is_active: true }, newData: { is_active: false }, at: daysAgo(1) },
  { id: "a7", actor: "Nairobi Threads", actorRole: "wholesaler", action: "INSERT", target: "products", recordId: null, oldData: null, newData: { name: "Puffer Jacket", price: 3200 }, at: daysAgo(1) },
  { id: "a8", actor: "system", actorRole: "system", action: "UPDATE", target: "orders", recordId: null, oldData: { status: "pending" }, newData: { status: "fulfilled" }, at: daysAgo(2) },
];
